import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BudgetBucketNodeItem, BudgetItem, BudgetVariant, DateTime, IdStartEnd } from "../../types";

function hydrateBucketNodes(nodes: BudgetBucketNodeItem[] | undefined): BudgetBucketNodeItem[] {
	return (nodes ?? []).map((n) => ({
		...n,
		startDate: n.startDate == null ? null : new DateTime(n.startDate as unknown as string),
		endDate: n.endDate == null ? null : new DateTime(n.endDate as unknown as string),
	}));
}

interface BudgetState {
	value: BudgetItem[] | undefined
}

const initialState: BudgetState = {
    value: []
};

export const budgetSlice = createSlice({
    name: 'budgets',
    initialState,
    reducers: {
        set: (state = initialState, action: PayloadAction<BudgetItem[] | undefined>) => {
            let value: BudgetItem[] = action?.payload ?? [];
            for (let i = 0; i < value.length; i++) {
                value[i] = {
                    ...value[i],
					variant: {
						amount: value[i].variant.amount,
						incomeAmount: value[i].variant.incomeAmount,
						budgetId: value[i].variant.budgetId,
						budgetVariantId: value[i].variant.budgetVariantId,
						endDate: value[i].variant.endDate === null ? null : new DateTime(value[i].variant.endDate),
						defaultEndDate: new DateTime(value[i].variant.defaultEndDate),
						isDefault: value[i].variant.isDefault,
						isInactive: value[i].variant.isInactive,
						timeframe: value[i].variant.timeframe,
						startDate: new DateTime(value[i].variant.startDate),
					} as BudgetVariant,
					activeTimeframes: value[i].activeTimeframes.map((atf: IdStartEnd) => {
						return {
							id: atf.id,
							startDate: atf.startDate == null ? null : new DateTime(atf.startDate),
							endDate: atf.endDate == null ? null : new DateTime(atf.endDate)
						} as IdStartEnd
					}),
					bucketNodes: hydrateBucketNodes(value[i].bucketNodes),
					endDate: new DateTime(value[i].endDate)
                }
            }
            state.value = value;
        },
        add: (state = initialState, action: PayloadAction<BudgetItem>) => {
            let value: BudgetItem[] = state.value ?? [];
            let newItem = action.payload as BudgetItem;
            newItem = {
                ...newItem,
				variant: {
					amount: newItem.variant.amount,
					incomeAmount: newItem.variant.incomeAmount,
					budgetId: newItem.variant.budgetId,
					budgetVariantId: newItem.variant.budgetVariantId,
					endDate: newItem.variant.endDate === null ? null : new DateTime(newItem.variant.endDate),
					defaultEndDate: newItem.variant.defaultEndDate,
					isDefault: newItem.variant.isDefault,
					isInactive: newItem.variant.isInactive,
					timeframe: newItem.variant.timeframe,
					startDate: new DateTime(newItem.variant.startDate)
				} as BudgetVariant,
				activeTimeframes: newItem.activeTimeframes.map((atf: IdStartEnd) => {
					return {
						id: atf.id,
						startDate: atf.startDate == null ? null : new DateTime(atf.startDate),
						endDate: atf.endDate == null ? null : new DateTime(atf.endDate)
					} as IdStartEnd
				}),
				bucketNodes: hydrateBucketNodes(newItem.bucketNodes),
				endDate: new DateTime(newItem.endDate)
            };
            state.value = [
                ...value, newItem
            ];
			return state;
        },
        remove: (state = initialState, action: PayloadAction<number>) => {
			let value: BudgetItem[] = state.value ?? [];
			state.value = [
                ...value.filter((item: BudgetItem) => {
                    return item.budgetId !== action.payload;
                })
            ];
			return state;
        }
    }
});

export const { set, add, remove } = budgetSlice.actions;

export default budgetSlice.reducer;